import * as React from 'react';
import { styled } from '@linaria/react';

import { clamp, normalize, roundTo } from '@/utils';

import SvgWithGrid from '@/components/SvgWithGrid';

import PointLabel from './PointLabel';

// See the note in LinearDemo to understand what the data format is for `points`.
type Point = number | string;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  points: Array<Point>;
  setPoints: React.Dispatch<React.SetStateAction<Array<Point>>>;
  headroom: number;
  drawingEnabled: boolean;
  includeHoverTooltip: boolean;
}

// How much space is reserved for the axis labels, in rems:
const LABEL_ALLOWANCE = 1.25;

function LinearGraph({
  points,
  setPoints,
  headroom,
  drawingEnabled,
  includeHoverTooltip,
  ...delegated
}: Props) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [hoveredPointIndex, setHoveredPointIndex] = React.useState<
    number | null
  >(null);

  const svgRef = React.useRef<SVGSVGElement>(null);

  const numOfPoints = points.length;
  const viewboxHeight = 100 + headroom;

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    function handlePointerMove(event: PointerEvent) {
      if (!isDragging || !svgRef.current) {
        return;
      }

      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();

      // Convert pointer coordinates to SVG coordinates
      const svgX = ((event.clientX - rect.left) / rect.width) * 100;
      const svgY =
        ((event.clientY - rect.top) / rect.height) * viewboxHeight;

      // Find the closest point based on horizontal position
      const columnWidth = 100 / (numOfPoints - 1);
      const targetColumn = Math.round(svgX / columnWidth);
      const clampedColumn = Math.max(
        0,
        Math.min(numOfPoints - 1, targetColumn)
      );

      // Update the point at the target column
      setPoints((prevPoints) => {
        const newPoints = [...prevPoints];
        const newY = clamp(viewboxHeight - svgY, 0, 100 + headroom);

        if (typeof newPoints[clampedColumn] === 'number') {
          newPoints[clampedColumn] = newY;
        } else {
          throw new Error(
            'Drawing only works with numbered points, not strings.'
          );
        }

        return newPoints;
      });
    }

    function handlePointerUp() {
      if (!drawingEnabled) {
        return;
      }

      setIsDragging(false);
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [
    isDragging,
    setPoints,
    drawingEnabled,
    headroom,
    viewboxHeight,
    numOfPoints,
  ]);

  // The input data can either be numbers, or strings like "50 20.5%".
  // Either way, I want it to be a set of X/Y coordinates, normalized for this graph.
  const normalizedPoints = points.map((point, index) => {
    if (typeof point === 'number') {
      const roundedPoint = roundTo(point, 2);
      const x = normalize(index, 0, numOfPoints - 1, 0, 100);
      const y = viewboxHeight - roundedPoint;

      return { x, y };
    } else {
      let [y, x] = point.split(' ');
      return {
        x: Number(x.replace('%', '')),
        y: viewboxHeight - Number(y),
      };
    }
  });

  const polylineData = normalizedPoints
    .map((point) => {
      return `${point.x},${point.y}`;
    })
    .join(' ');

  const handlePointerDown = React.useCallback(() => {
    if (!drawingEnabled) {
      return;
    }

    setIsDragging(true);
  }, [drawingEnabled]);

  return (
    <OuterWrapper data-can-be-annotated={false}>
      <YLabel>
        <span>— Progress —</span>
      </YLabel>
      <XLabel>
        <span>— Time —</span>
      </XLabel>
      <Wrapper {...delegated}>
        <GraphWrapper>
          <Graph
            ref={svgRef}
            viewBox={`0 0 100 ${viewboxHeight}`}
            gridGap={10}
            lineWidth={1}
            gridColor="var(--color-gray-300)"
            data-drawing-enabled={drawingEnabled}
            onPointerDown={handlePointerDown}
          >
            <rect
              x="0"
              y="0"
              width="100"
              height={viewboxHeight}
              rx={1}
              stroke="var(--color-gray-300)"
              strokeWidth={0.2}
              fill="none"
            />

            {headroom > 0 && (
              <line
                x1="0"
                y1={headroom}
                x2="100"
                y2={headroom}
                stroke="var(--color-gray-300)"
              />
            )}
            <polyline
              points={polylineData}
              stroke="white"
              strokeWidth={1}
              fill="none"
            />
            {normalizedPoints.map(({ x, y }, index) => {
              const isHovered = hoveredPointIndex === index;
              const isAnotherPointHovered =
                hoveredPointIndex !== null &&
                hoveredPointIndex !== index;

              return (
                <PointCircle
                  key={index}
                  cx={x}
                  cy={y}
                  r={isHovered ? 3 : 1.5}
                  fill="var(--wham-primary-500)"
                  stroke="var(--color-background)"
                  strokeWidth={0.5}
                  data-is-hovered={isHovered}
                  data-is-another-point-hovered={
                    isAnotherPointHovered
                  }
                  style={{
                    '--index': index,
                  }}
                />
              );
            })}
            {/* Big invisible circles, used purely for hover events: */}
            {includeHoverTooltip &&
              normalizedPoints.map(({ x, y }, index) => (
                <circle
                  key={index}
                  cx={x}
                  cy={y}
                  r={5}
                  fill="transparent"
                  onMouseEnter={() => {
                    if (includeHoverTooltip) {
                      setHoveredPointIndex(index);
                    }
                  }}
                  onMouseLeave={() => {
                    if (includeHoverTooltip) {
                      setHoveredPointIndex(null);
                    }
                  }}
                />
              ))}
          </Graph>
          {hoveredPointIndex !== null && (
            <PointLabel
              key={hoveredPointIndex}
              headroom={headroom}
              hoveredPoint={normalizedPoints[hoveredPointIndex]}
              rawPoint={points[hoveredPointIndex]}
            />
          )}
        </GraphWrapper>
      </Wrapper>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  display: grid;
  grid-template-columns: ${LABEL_ALLOWANCE}rem 1fr ${LABEL_ALLOWANCE}rem;
  grid-template-rows: 1fr ${LABEL_ALLOWANCE}rem;
  grid-template-areas:
    'ylabel graph  spacer'
    '.      xlabel .';
`;

const Wrapper = styled.div`
  grid-area: graph;
  border: 3px solid var(--color-gray-300);
  padding: 8px;
  border-radius: 6px;
`;

const GraphWrapper = styled.div`
  position: relative;
`;
const Graph = styled(SvgWithGrid)`
  display: block;
  position: relative;
  outline-offset: -1px;
  stroke-linecap: round;
  stroke-linejoin: round;
  overflow: visible;

  &[data-drawing-enabled='true'] {
    touch-action: none;
    cursor:
      url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='hsl(210deg 15% 6%)' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z'/><path d='m15 5 4 4'/></svg>")
        0 20,
      crosshair;
  }
`;

const PointCircle = styled.circle`
  /* prettier-ignore */
  --spring: linear(0, 0.027 1.4%, 0.108 3%, 0.615 9.6%, 0.824 13%, 0.963 16.6%, 1.035 20.5%, 1.05 22.8%, 1.052 25.6%, 1.01 37.4%, 0.998 45.6%, 1);

  opacity: 1;
  transition:
    opacity 200ms,
    r 1000ms var(--spring);

  &[data-is-another-point-hovered='true'] {
    --transition-delay: calc(var(--index) * 5ms);
    opacity: 0;
  }

  &[data-is-hovered='true'] {
    transition:
      opacity 200ms,
      r 1000ms var(--spring);

    opacity: 1;
  }
`;

const AxisLabel = styled.div`
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-700);
  text-transform: uppercase;
  letter-spacing: 1px;
  user-select: none;
`;

const YLabel = styled(AxisLabel)`
  grid-area: ylabel;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  span {
    display: block;
    white-space: nowrap;
    transform: translateX(-0.2rem) rotate(-90deg);
    transform-origin: center;
  }
`;

const XLabel = styled(AxisLabel)`
  grid-area: xlabel;
  display: flex;
  justify-content: center;
  transform: translateY(0.25rem);
`;

export default LinearGraph;
